/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {Injectable} from '@angular/core';
import {DashGisUtil} from '../../../modules/shared/dash-gis-util';
import {Observable} from 'rxjs';
import {RedlineState} from '../state/redline.state';
import {Select, Store} from '@ngxs/store';
import {RedlineLayerConfiguration} from '../state/redline.model';
import {first, map, take} from 'rxjs/operators';
import {TurnRedlineLayerOff, TurnRedlineLayerOn} from '../state/redline.actions';
import {LoggerService} from '../../../modules/shared/services/logger.service';

@Injectable()
export class RedlineConfigService {

    @Select(RedlineState.viewable) public viewableLayers$: Observable<RedlineLayerConfiguration[]>;
    @Select(RedlineState.editable) public editableLayers$: Observable<RedlineLayerConfiguration[]>;

    constructor(
        private logger: LoggerService,
        private store: Store,
    ) {}

    /**
     * Get the redline layers which are actually visible
     * @param scale {int} The scale denomination the layers should be visible at. If this evaluates to false,
     *                    return all layers.
     * @returns {Array}
     */
    public getVisibleLayerList$(scale): Observable<RedlineLayerConfiguration[]> {
        return this.viewableLayers$.pipe(
            map((viewable) => viewable.filter(x => x.status)),
            map((visibleLayers) => scale ? this.removeOutOfRangeRedlines(visibleLayers, scale) : visibleLayers),
            take(1),
        );
    }

    /**
     * Get the redline layers which are potentially visible
     * @param scale {int} The scale denomination the layers should be visible at. If this evaluates to false,
     *                    return all layers.
     * @returns {Array}
     */
    public getInRangeLayerList$(scale): Observable<RedlineLayerConfiguration[]> {
        return this.viewableLayers$.pipe(
            map((viewable) => DashGisUtil.clone(viewable)),
            map((viewable) => scale ? this.removeOutOfRangeRedlines(viewable, scale) : viewable),
            take(1),
        );
    }

    public getLayer$(layerName): Observable<RedlineLayerConfiguration> {
        return this.viewableLayers$.pipe(
            map((viewable: RedlineLayerConfiguration[]) => viewable.find((l) => l.name === layerName)),
            take(1),
        );
    }

    public showLayer(layerName) {
        this.viewableLayers$.pipe(
            first(),
            map((viewable) => viewable.find((l) => l.name === layerName)),
        ).subscribe((layer) => this.store.dispatch(new TurnRedlineLayerOn(layer.name)));
    }

    public hideLayer(layerName) {
        this.viewableLayers$.pipe(
            first(),
            map((viewable) => viewable.find((l) => l.name === layerName)),
        ).subscribe((layer) => this.store.dispatch(new TurnRedlineLayerOff(layer.name)));
    }

    /**
     *
     * @param redlineLayers {Array}
     * @param scale {int}
     * @returns {Array}
     */
    private removeOutOfRangeRedlines(redlineLayers, scale) {
        for (let i = redlineLayers.length - 1; i >= 0; i--) {
            const item = redlineLayers[i];
            if (item.minScale > scale || item.maxScale < scale) {
                redlineLayers.splice(i, 1);
            }
        }
        return redlineLayers;
    }
}
